import { formatDistanceToNow } from 'date-fns';

import { Box, Grid, Heading, Text, useTheme } from '@simplywallst/ui-core';
import LikeOutline from '@simplywallst/ui-core/icons/LikeOutline';
import CommentOutline from '@simplywallst/ui-core/icons/CommentOutline';
import UserProfileOutline from '@simplywallst/ui-core/icons/UserProfileOutline';
import { toCurrencyTruncated, toTruncated } from '@/utilities/formatting';
import { CompanyIntrinsicDiscount } from '@/components/CompanyIntrinsicDiscount';
import { KeyMetric } from '@/components/KeyMetric';
import { NarrativeAuthor } from '@/components/NarrativeAuthor';
// import { FollowNarrative } from '@/pages/Narrative/components/FollowNarrative'; NOTE: turn off FollowNarrative temporarily

import { ROUTE_NARRATIVE } from '@/constants/routes';
import { useFastCommentsCount } from '@components/FastCommentWidget';

import { HiddenText, StyledRouterLink } from './styled';
import { FragmentOf, readFragment } from 'gql.tada';
import { NARRATIVE_CARD_FRAGMENT } from './narrativeCardFragment';
import { HTMLAttributeAnchorTarget, ReactNode } from 'react';
import { RouterLinkAction } from '@/utilities/redux-first-router.export';

export type PopulatedStateBaseProps = {
  onClick?: () => void;
  children?: ReactNode;
  linkTarget?: HTMLAttributeAnchorTarget;
  linkTo?: string | RouterLinkAction | undefined;
};

type Props = PopulatedStateBaseProps & {
  narrative: FragmentOf<typeof NARRATIVE_CARD_FRAGMENT>;
};

export const PopulatedState = ({
  narrative,
  children,
  onClick,
  linkTarget = '_self',
  linkTo,
}: Props) => {
  const theme = useTheme();
  const {
    company,
    currencyIso,
    followCount,
    slug,
    updatedAt,
    title,
    userHoldsPosition,
    author,
    valuation,
    voteCount,
  } = readFragment(NARRATIVE_CARD_FRAGMENT, narrative);
  const { commentCount } = useFastCommentsCount(`/narratives/${slug}`);
  const { analysisValue } = company ?? {};

  return (
    <StyledRouterLink
      to={linkTo ?? { type: ROUTE_NARRATIVE, payload: { slug } }}
      noUnderline
      onClick={onClick}
      target={linkTarget}
    >
      <Box borderRadius="x1" overflow="hidden">
        <Grid backgroundColor="spaceGray" padding="x2" gap={`${theme.x2}px`}>
          <Grid
            templateColumns="max-content max-content"
            justifyContent="space-between"
          >
            <NarrativeAuthor
              author={author}
              userHoldsPosition={userHoldsPosition}
            />
            {/* <FollowNarrative narrative={narrative} /> NOTE: turn off FollowNarrative temporarily */}
          </Grid>
          <Box minHeight="81px">
            <Heading
              as="h4"
              typography="baseStrong"
              lineClamp={3}
              color="textSolid"
            >
              {title}
            </Heading>
          </Box>
        </Grid>
        <Grid backgroundColor="phantom" padding="x2" gap={`${theme.x2}px`}>
          <Grid templateColumns="1fr 1fr">
            <KeyMetric color="chart03">
              <Grid templateRows="max-content max-content">
                <Text typography="largeStrong" color="textSolid">
                  {toCurrencyTruncated(
                    valuation?.fairValue || 0,
                    1,
                    currencyIso ?? 'XXX'
                  )}
                </Text>
                <Grid templateColumns="max-content max-content">
                  <Text typography="xSmall" color="textSoft">
                    FV&nbsp;&nbsp;
                  </Text>
                  <Text as="span" typography="tiny" color="textSoft">
                    <CompanyIntrinsicDiscount
                      lastSharePrice={analysisValue?.lastSharePrice ?? null}
                      npvPerShare={analysisValue?.npvPerShare ?? null}
                      fairValue={valuation?.fairValue}
                    />
                  </Text>
                </Grid>
              </Grid>
            </KeyMetric>
            {valuation?.revenuePercentageChange !== null && (
              <KeyMetric color="brand01">
                <Grid templateRows="max-content max-content">
                  <Text typography="largeStrong" color="textSolid">
                    {toTruncated(valuation?.revenuePercentageChange || 0, 2)}%
                  </Text>
                  <Text typography="xSmall" color="textSoft">
                    Revenue growth p.a.
                  </Text>
                </Grid>
              </KeyMetric>
            )}
          </Grid>
          {children}
          <Grid
            templateColumns="1fr 1fr"
            gap={`${theme.x2}px`}
            justifyContent="space-between"
          >
            <Grid
              templateColumns="max-content max-content max-content"
              gap={`${theme.x2}px`}
              justifyContent="start"
            >
              <Grid
                templateColumns="min-content min-content"
                placeContent="center"
                gap={`${theme.x0_5}px`}
              >
                <Grid placeContent="center">
                  <LikeOutline
                    fill={theme.color.mediumGray}
                    width={theme.x2}
                    height={theme.x2}
                  />
                </Grid>
                <Text typography="xSmall" color="textSofter">
                  {voteCount.upvotes}
                  <HiddenText>users have liked this narrative</HiddenText>
                </Text>
              </Grid>
              <Grid
                templateColumns="min-content min-content"
                placeContent="center"
                gap={`${theme.x0_5}px`}
              >
                <Grid placeContent="center">
                  <CommentOutline
                    fill={theme.color.mediumGray}
                    width={theme.x2}
                    height={theme.x2}
                  />
                </Grid>
                <Text typography="xSmall" color="textSofter">
                  {commentCount}
                  <HiddenText>
                    users have commented on this narrative
                  </HiddenText>
                </Text>
              </Grid>
              <Grid
                templateColumns="min-content min-content"
                placeContent="center"
                gap={`${theme.x0_5}px`}
              >
                <Grid placeContent="center">
                  <UserProfileOutline
                    fill={theme.color.mediumGray}
                    width={theme.x2}
                    height={theme.x2}
                  />
                </Grid>
                <Text typography="xSmall" color="textSofter">
                  {followCount}
                  <HiddenText>users have followed this narrative</HiddenText>
                </Text>
              </Grid>
            </Grid>
            <Grid
              templateColumns="max-content"
              gap={`${theme.x2}px`}
              justifyContent="end"
            >
              {updatedAt !== null && (
                <Text typography="xSmall" color="textSofter">
                  {formatDistanceToNow(new Date(updatedAt), {
                    addSuffix: true,
                  })}
                  <HiddenText>author updated this narrative</HiddenText>
                </Text>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </StyledRouterLink>
  );
};
